import { z } from 'zod';

/**
 * Editorial registers for external research (ADR-041). The received-law register records
 * English statutes and rules and whether they apply in Sierra Leone; only a legal reviewer may
 * record that applicability is established. Doctrine records are short, reviewed notes on a
 * doctrine, given to the AI as guidance (never cited) when external research concerns it.
 */

const text = (max: number) => z.string().trim().max(max);

export const ReceivedLawStatusSchema = z.enum(['unconfirmed', 'established', 'displaced']);
export type ReceivedLawStatus = z.infer<typeof ReceivedLawStatusSchema>;

export const RECEIVED_LAW_STATUS_LABEL: Record<ReceivedLawStatus, string> = {
  unconfirmed: 'Applicability requires confirmation',
  established: 'Applicability established',
  displaced: 'Displaced or modified by Sierra Leone law',
};

export const ReceivedLawInputSchema = z
  .object({
    name: text(200).min(3),
    kind: z.enum(['statute', 'common_law', 'equity', 'other']),
    originalJurisdiction: text(120).min(2),
    date: text(40),
    /** A legislation.gov.uk address prefix the entry applies to, or empty. */
    urlPrefix: text(300).refine(
      (v) => v === '' || /^https:\/\/(www\.)?legislation\.gov\.uk\//.test(v),
      'Use a legislation.gov.uk address, or leave it empty',
    ),
    receptionBasis: text(1000).min(3),
    sierraLeoneAuthority: text(1000),
    laterModification: text(1000),
    notes: text(2000),
    status: ReceivedLawStatusSchema,
  })
  .strict();
export type ReceivedLawInput = z.infer<typeof ReceivedLawInputSchema>;

export const ReceivedLawRecordSchema = ReceivedLawInputSchema.extend({
  id: z.string(),
  createdByName: z.string().nullable(),
  updatedAt: z.string(),
  /** Who set the current status, when it is not "unconfirmed". */
  reviewedByName: z.string().nullable(),
  reviewedAt: z.string().nullable(),
});
export type ReceivedLawRecord = z.infer<typeof ReceivedLawRecordSchema>;

const authority = z
  .object({
    name: text(200).min(2),
    citation: text(200),
    jurisdiction: text(120),
  })
  .strict();

export const DoctrineInputSchema = z
  .object({
    name: text(120).min(3),
    description: text(2000).min(10),
    sierraLeoneRelevance: text(2000),
    leadingAuthorities: z.array(authority).max(10),
    sierraLeoneCases: z.array(authority).max(10),
    notes: text(2000),
    reviewDate: text(20),
  })
  .strict();
export type DoctrineInput = z.infer<typeof DoctrineInputSchema>;

export const DoctrineRecordSchema = DoctrineInputSchema.extend({
  id: z.string(),
  status: z.enum(['draft', 'approved', 'retired']),
  createdByUid: z.string(),
  createdByName: z.string().nullable(),
  updatedAt: z.string(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
});
export type DoctrineRecord = z.infer<typeof DoctrineRecordSchema>;

export const RegisterListSchema = <T extends z.ZodType>(item: T) =>
  z.object({ entries: z.array(item), canEdit: z.boolean(), canApprove: z.boolean() });
export const ReceivedLawListSchema = RegisterListSchema(ReceivedLawRecordSchema);
export type ReceivedLawList = z.infer<typeof ReceivedLawListSchema>;
export const DoctrineListSchema = RegisterListSchema(DoctrineRecordSchema);
export type DoctrineList = z.infer<typeof DoctrineListSchema>;

export const DoctrineDecisionSchema = z
  .object({ decision: z.enum(['approve', 'retire']) })
  .strict();
